import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Avatar,
  TableRow,
  TableCell,
  Checkbox,
  IconButton,
  Popover,
  MenuItem,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress
} from '@mui/material';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { type EmployeeDto, approveEmployee, banEmployee } from 'src/api/api_data';
import { positionLabelTr } from 'src/utils/labels';

// ----------------------------------------------------------------------

// Durum rozeti: backend'in hesapladigi awaitingDeviceApproval alani ilk cihaz onayi bekleyenleri
// isaretler -> turuncu "Onay Bekliyor". Diger tum INACTIVE durumlar (vardiya disi, otomatik
// onaylanan cihaz degisikligi vb.) admin eylemi gerektirmez -> notr gri "Pasif". BANNED (Yasakli)
// ayri ve kirmizi kalir; bu admin/guvenlik kaynakli gercek engelleme durumudur.
function getStatusDisplay(status: string, awaitingDeviceApproval: boolean): { label: string; color: 'success' | 'warning' | 'error' | 'default' } {
  if (status === 'ACTIVE') return { label: 'Aktif', color: 'success' };
  if (status === 'INACTIVE' && awaitingDeviceApproval) return { label: 'Onay Bekliyor', color: 'warning' };
  if (status === 'INACTIVE') return { label: 'Pasif', color: 'default' };
  if (status === 'ON_LEAVE') return { label: 'Izinde', color: 'warning' };
  if (status === 'BANNED') return { label: 'Yasakli', color: 'error' };
  return { label: status, color: 'default' };
}

interface BranchEmployeeTableRowProps {
  employee: EmployeeDto;
  selected: boolean;
  onSelectRow: () => void;
  onStatusChange: () => void;
}

export default function BranchEmployeeTableRow({ employee, selected, onSelectRow, onStatusChange }: BranchEmployeeTableRowProps) {
  const navigate = useNavigate();
  const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const isAwaitingApproval = Boolean(employee.awaitingDeviceApproval);
  const statusDisplay = getStatusDisplay(employee.status, isAwaitingApproval);
  const awaitingApprovalHint = 'İlk cihaz kaydı, onay bekleniyor';

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      await approveEmployee(employee.id);
      onStatusChange();
    } catch (err) {
      console.error('Onaylama hatasi:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setActionLoading(true);
      await banEmployee(employee.id);
      setRejectDialogOpen(false);
      onStatusChange();
    } catch (err) {
      console.error('Reddetme hatasi:', err);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <TableRow hover selected={selected}>
        <TableCell padding="checkbox">
          <Checkbox
            disableRipple
            checked={selected}
            onChange={onSelectRow}
          />
        </TableCell>
        
        <TableCell component="th" scope="row">
          <Box gap={2} display="flex" alignItems="center">
            <Avatar alt={`${employee.firstName} ${employee.lastName}`} />
            <Typography variant="subtitle2" noWrap>
              {`${employee.firstName} ${employee.lastName}`}
            </Typography>
          </Box>
        </TableCell>

        <TableCell>{employee.email}</TableCell>

        <TableCell>{positionLabelTr(employee.position)}</TableCell>

        <TableCell>
          {isAwaitingApproval ? (
            <Tooltip title={awaitingApprovalHint}>
              <span>
                <Label color={statusDisplay.color}>
                  {statusDisplay.label}
                </Label>
              </span>
            </Tooltip>
          ) : (
            <Label color={statusDisplay.color}>
              {statusDisplay.label}
            </Label>
          )}
        </TableCell>

        <TableCell align="center">
          {employee.verified ? (
            <Iconify width={22} icon="solar:check-circle-bold" sx={{ color: 'success.main' }} />
          ) : (
            <Iconify width={22} icon="solar:close-circle-bold" sx={{ color: 'error.main' }} />
          )}
        </TableCell>

        <TableCell align="right">
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
            {isAwaitingApproval && (
              <>
                <Tooltip title="Onayla">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={handleApprove}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Iconify icon="solar:check-circle-bold" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reddet">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setRejectDialogOpen(true)}
                    disabled={actionLoading}
                  >
                    <Iconify icon="solar:close-circle-bold" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <IconButton onClick={handleOpenPopover}>
              <Iconify icon="eva:more-vertical-fill" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>

      <Popover
        open={!!openPopover}
        anchorEl={openPopover}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            navigate(`/dashboard/employee/${employee.id}`);
            handleClosePopover();
          }}
        >
          <Iconify icon="solar:eye-bold" />
          Detayları Görüntüle
        </MenuItem>
        
        <MenuItem
          onClick={() => {
            navigate(`/dashboard/employee/${employee.id}/edit`);
            handleClosePopover();
          }}
        >
          <Iconify icon="solar:pen-bold" />
          Düzenle
        </MenuItem>
      </Popover>

      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
      >
        <DialogTitle>Calisani Reddet</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`${employee.firstName} ${employee.lastName} adli calisanin basvurusunu reddetmek istediginize emin misiniz? Bu islem calisanin hesabini yasaklayacaktir.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={actionLoading}>
            Iptal
          </Button>
          <Button onClick={handleReject} color="error" variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Reddet'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}